import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Check, Sparkles } from "lucide-react";
import { devWarn } from "../lib/logger";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const verified = params.get("verified") === "1";
  const [status, setStatus] = useState(verified && !sessionId ? "paid" : "checking");
  const [credits, setCredits] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    // Razorpay/PayPal flow — verification already done on the pricing page.
    if (verified && !sessionId) {
      let cancelled = false;
      (async () => {
        try {
          await refresh();
          if (!cancelled) {
            // Read credits from /auth/me for display
            const { data } = await api.get("/auth/me");
            if (!cancelled) setCredits(data.credits);
          }
        } catch (err) {
          devWarn("Refresh after verified payment failed", err);
        }
      })();
      return () => { cancelled = true; };
    }
    // Legacy Stripe flow — poll session status.
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setCredits(data.credits);
          await refresh();
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch (err) {
        devWarn("Payment status poll failed", err);
      }
      pollCount.current += 1;
      if (pollCount.current > 20) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, verified, refresh]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6">
      <div className="max-w-lg text-center">
        {status === "checking" && (
          <>
            <svg className="glyph-loader mx-auto mb-8" width="80" height="80" viewBox="0 0 80 80" fill="none">
              <circle cx="40" cy="40" r="34" stroke="#D4AF37" strokeWidth="1" strokeDasharray="4 6" />
              <circle cx="40" cy="40" r="22" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />
              <circle cx="40" cy="40" r="4" fill="#D4AF37" />
            </svg>
            <h1 className="font-serif text-3xl tracking-tight mb-3">Sealing your credits…</h1>
            <p className="text-neutral-400">Confirming your payment. This takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <div className="fade-up">
            <div className="mx-auto mb-8 grid h-16 w-16 place-items-center rounded-full border border-gold/50 bg-gold/10">
              <Check className="h-8 w-8 text-gold" />
            </div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">The ledger is written</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mb-4" data-testid="success-heading">Credits granted.</h1>
            <p className="text-neutral-400 mb-8 flex items-center justify-center gap-2">
              <Sparkles className="h-4 w-4 text-gold" /> Your balance is now {credits ?? "updated"}.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/ask">
                <Button data-testid="continue-btn" className="rounded-full bg-gold text-black hover:bg-[#F3E5AB] px-8 transition-colors duration-200">
                  Ask a question
                </Button>
              </Link>
              <Link to="/history">
                <Button variant="outline" className="rounded-full border-white/20 bg-transparent hover:bg-white/5 hover:text-white transition-colors duration-200">
                  See readings
                </Button>
              </Link>
            </div>
          </div>
        )}
        {(status === "failed" || status === "timeout") && (
          <div>
            <h1 className="font-serif text-3xl tracking-tight mb-3">Payment not confirmed</h1>
            <p className="text-neutral-400 mb-8">
              {status === "failed" ? "The transaction did not complete." : "Still processing — please refresh in a minute or contact support."}
            </p>
            <Button onClick={() => nav("/pricing")} className="rounded-full bg-gold text-black hover:bg-[#F3E5AB] transition-colors duration-200">
              Back to pricing
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
